import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme,
  Avatar,
  Skeleton
} from '@mui/material'
import {
  TableChart as TableIcon,
  Visibility as ViewIcon,
  Storage as StorageIcon,
  Assessment as StatsIcon,
  AccessTime as TimeIcon,
  DataObject as DataIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material'
import { useAuthContext } from '../../contexts/AuthContext'
import { config } from '../../config/environment'

interface DatabaseStats {
  tables: number
  views: number
  indexes: number
  totalRows: number
  databaseSize: string
  lastModified: string
}

interface TableInfo {
  name: string
  type: 'table' | 'view'
  rowCount: number
  columns: Array<{
    name: string
    type: string
    primaryKey: boolean
  }>
}

interface DatabaseOverviewProps {
  onError: (error: string | null) => void
  refreshTrigger: boolean
}

/**
 * Database Overview Component
 * Shows database statistics, recent tables, and quick insights
 */
const DatabaseOverview: React.FC<DatabaseOverviewProps> = ({ onError, refreshTrigger }) => {
  const theme = useTheme()
  const { user } = useAuthContext()
  const [stats, setStats] = useState<DatabaseStats | null>(null)
  const [tables, setTables] = useState<TableInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Get API base URL
  const getApiBaseUrl = () => {
    return config.API_BASE_URL
  }

  const fetchDatabaseStats = async () => {
    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) {
        onError('Authentication token not found')
        return
      }

      const response = await fetch(`${getApiBaseUrl()}/api/db-admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch database stats')
      }

      setStats(data.data)
    } catch (error) {
      console.error('Error fetching database stats:', error)
      onError(error instanceof Error ? error.message : 'Failed to fetch database statistics')
    }
  }

  const fetchTables = async () => {
    try {
      const token = localStorage.getItem('dashboard_access_token')
      if (!token) return

      const response = await fetch(`${getApiBaseUrl()}/api/db-admin/tables`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch tables')
      }

      setTables(data.data.slice(0, 10)) // Show only first 10 tables in overview
    } catch (error) {
      console.error('Error fetching tables:', error)
      onError(error instanceof Error ? error.message : 'Failed to fetch tables')
    }
  }

  const loadData = async () => {
    setIsLoading(true)
    onError(null)
    
    try {
      await Promise.all([
        fetchDatabaseStats(),
        fetchTables()
      ])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [refreshTrigger])

  const formatLastModified = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleString()
    } catch {
      return 'Unknown'
    }
  }

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    subtitle 
  }: { 
    title: string
    value: string | number
    icon: React.ReactElement
    color: string
    subtitle?: string 
  }) => (
    <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h4" component="div" fontWeight="bold">
              {isLoading ? <Skeleton width={60} /> : value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {isLoading ? <Skeleton width={100} /> : subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  return (
    <Box>
      {/* Database Statistics */}
      <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Database Statistics
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Tables"
            value={stats?.tables || 0}
            icon={<TableIcon />}
            color={theme.palette.primary.main}
            subtitle="Data tables"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Views"
            value={stats?.views || 0}
            icon={<ViewIcon />}
            color={theme.palette.secondary.main}
            subtitle="Database views"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Rows"
            value={stats?.totalRows.toLocaleString() || 0}
            icon={<DataIcon />}
            color={theme.palette.success.main}
            subtitle="Across all tables"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Database Size"
            value={stats?.databaseSize || '0 MB'}
            icon={<StorageIcon />}
            color={theme.palette.warning.main}
            subtitle="File size"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Database Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Database Information
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Database Type"
                    secondary="SQLite"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <TimeIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Last Modified"
                    secondary={isLoading ? <Skeleton width={150} /> : formatLastModified(stats?.lastModified || '')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <StatsIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Indexes"
                    secondary={isLoading ? <Skeleton width={50} /> : `${stats?.indexes || 0} indexes for optimized queries`}
                  />
                </ListItem>
              </List>
            </CardContent>
            
            <CardActions>
              <Button size="small" onClick={loadData} disabled={isLoading}>
                Refresh Stats
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Recent Tables */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" component="h3" gutterBottom>
                Tables Overview
              </Typography>
              
              {isLoading ? (
                <Box>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton width="60%" />
                        <Skeleton width="40%" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : tables.length > 0 ? (
                <List>
                  {tables.map((table, index) => (
                    <ListItem key={table.name} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Avatar sx={{ 
                          bgcolor: table.type === 'table' ? theme.palette.primary.main : theme.palette.secondary.main,
                          width: 32,
                          height: 32 
                        }}>
                          {table.type === 'table' ? <TableIcon sx={{ fontSize: 18 }} /> : <ViewIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {table.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={table.type}
                              variant="outlined"
                              color={table.type === 'table' ? 'primary' : 'secondary'}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {table.rowCount.toLocaleString()} rows • {table.columns.length} columns
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No tables found in database
                </Typography>
              )}
            </CardContent>
            
            {tables.length > 0 && (
              <CardActions>
                <Button size="small">
                  View All Tables
                </Button>
              </CardActions>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: theme.palette.action.hover }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            startIcon={<TableIcon />}
            onClick={() => {/* Navigate to tables tab */}}
          >
            Browse Tables
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<TrendingUpIcon />}
            onClick={() => {/* Navigate to query tab */}}
          >
            Run Query
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<StorageIcon />}
            onClick={loadData}
            disabled={isLoading}
          >
            Refresh Data
          </Button>
        </Box>
      </Paper>
    </Box>
  )
}

export default DatabaseOverview